/**
 * chain-eos/avatar-resolve.ts — turn on-chain avatar declarations into images.
 *
 * The `properavatar` contract stores an intent ("this account picked asset N"),
 * not a fact. It cannot check ownership, because ownership changes after the row
 * is written: people sell the NFT, or burn it, or transfer it to a friend. So the
 * declaration is only half the answer — resolving it means asking AtomicAssets
 * who owns that asset right now and refusing to render it for anyone else.
 *
 * Without that check the contract would be a free billboard: write one row and
 * put any picture you like next to any account name.
 */

import { fetchAvatarChoices } from './avatar.js'
import { loadNftsByIds } from './nft.js'

export interface ResolvedAvatar {
  account: string
  assetId: string
  mediaUrl: string
  mediaType: 'image' | 'video' | 'unknown'
}

const TTL_MS = 5 * 60_000

interface CacheEntry {
  value: ResolvedAvatar | null
  at: number
}

const cache = new Map<string, CacheEntry>()

/** Drops one account (or everything) from the cache — call after setavatar/clear. */
export function invalidateAvatarCache(account?: string): void {
  if (account) cache.delete(account.trim())
  else cache.clear()
}

/**
 * Resolves avatars for a batch of accounts. Two network calls total regardless of
 * how many accounts are asked for, plus whatever is already cached.
 */
export async function resolveAvatars(accounts: string[]): Promise<Map<string, ResolvedAvatar>> {
  const out = new Map<string, ResolvedAvatar>()
  const now = Date.now()
  const stale: string[] = []

  for (const raw of new Set(accounts.map(a => a.trim()).filter(Boolean))) {
    const hit = cache.get(raw)
    if (hit && now - hit.at < TTL_MS) {
      if (hit.value) out.set(raw, hit.value)
    } else {
      stale.push(raw)
    }
  }
  if (stale.length === 0) return out

  // Remember a miss too, so a list of accounts without avatars stops re-asking.
  const remember = (account: string, value: ResolvedAvatar | null) => {
    cache.set(account, { value, at: Date.now() })
    if (value) out.set(account, value)
  }

  try {
    const choices = await fetchAvatarChoices(stale, { limit: stale.length })
    const assetIds = [...choices.values()].map(c => c.assetId)
    const assets = assetIds.length > 0 ? await loadNftsByIds(assetIds) : []
    const byId = new Map(assets.map(a => [a.assetId, a]))

    for (const account of stale) {
      const choice = choices.get(account)
      if (!choice) {
        remember(account, null)
        continue
      }
      const asset = byId.get(choice.assetId)
      // The ownership check. `owner` empty means the API did not tell us, and an
      // unverified claim is not good enough to show to other people.
      if (!asset || !asset.mediaUrl || asset.owner !== account) {
        remember(account, null)
        continue
      }
      remember(account, {
        account,
        assetId: asset.assetId,
        mediaUrl: asset.mediaUrl,
        mediaType: asset.mediaType,
      })
    }
  } catch {
    // Leave the cache alone: a network blip should not pin "no avatar" for 5 min.
  }
  return out
}

/** Single-account convenience wrapper. */
export async function resolveAvatar(account: string): Promise<ResolvedAvatar | null> {
  return (await resolveAvatars([account])).get(account.trim()) ?? null
}
